import React, { createContext, useContext, useMemo } from 'react';
import { useGlobal } from '@/app/providers/global-provider';
import type { AqlAssistantContext, AqlAssistantPage, AqlAssistantTimeWindow } from './assistantApi';

type OperationalContextInput = Partial<AqlAssistantContext>;

const AqlOperationalContext = createContext<AqlAssistantContext | null>(null);

export function AqlOperationalContextProvider({
  value,
  children
}: {
  value?: OperationalContextInput;
  children: React.ReactNode;
}) {
  const { client, species, phase, site, tank } = useGlobal();

  const context = useMemo<AqlAssistantContext>(() => {
    const currentPage: AqlAssistantPage = value?.current_page ?? 'dashboard';
    const timeWindow: AqlAssistantTimeWindow = value?.time_window ?? (currentPage === 'dashboard' ? '24h' : '30d');

    return {
      client_id: value?.client_id ?? client?.id ?? null,
      project_id: value?.project_id ?? null,
      location_id: value?.location_id ?? null,
      zone_id: value?.zone_id ?? null,
      kit_id: value?.kit_id ?? null,
      camera_id: value?.camera_id ?? null,
      species_id: value?.species_id ?? null,
      phase_id: value?.phase_id ?? null,
      cohort_id: value?.cohort_id ?? null,
      time_window: timeWindow,
      current_page: currentPage,
      client_name: value?.client_name ?? client?.name ?? null,
      project_name: value?.project_name ?? null,
      location_label: value?.location_label ?? site ?? null,
      zone_label: value?.zone_label ?? tank ?? null,
      kit_label: value?.kit_label ?? null,
      camera_label: value?.camera_label ?? null,
      species_label: value?.species_label ?? species ?? client?.species_focus ?? null,
      phase_label: value?.phase_label ?? phase ?? client?.default_phase ?? null,
      cohort_label: value?.cohort_label ?? null,
      language: value?.language ?? null
    };
  }, [client, phase, site, species, tank, value]);

  return (
    <AqlOperationalContext.Provider value={context}>
      {children}
    </AqlOperationalContext.Provider>
  );
}

export function useAqlOperationalContext() {
  const context = useContext(AqlOperationalContext);
  if (!context) {
    throw new Error('useAqlOperationalContext must be used within AqlOperationalContextProvider.');
  }
  return context;
}

const pageLabel: Record<AqlAssistantPage, string> = {
  dashboard: 'Dashboard',
  alerts: 'Alerts',
  analyses: 'Analyses',
  growth: 'Growth',
  camera_live: 'Camera Live'
};

export function getOperationalContextChips(context: AqlAssistantContext) {
  return [
    { label: 'Client', value: context.client_name },
    { label: 'Page', value: pageLabel[context.current_page] },
    { label: 'Species', value: context.species_label },
    { label: 'Phase', value: context.phase_label },
    { label: 'Zone', value: context.zone_label },
    { label: 'Kit', value: context.kit_label },
    { label: 'Window', value: context.time_window }
  ].filter((item): item is { label: string; value: string } => Boolean(item.value));
}
